import { type Audience, PROBLEM } from "@/content";

/** Warm amber for the pain icons — distinct from the magenta brand accent. */
const PAIN_TINT = "oklch(60% 0.16 45)";

type ProblemProps = { audience: Audience };

/**
 * Problem section — the pains adapt to the audience (see content.ts). The
 * printer's signature pain (a badly-built file) is theirs alone; the broker's
 * pains are about coordinating outsourced work. Names the friction before
 * selling the cure.
 */
export function Problem({ audience }: ProblemProps) {
  const { title, intro, pains } = PROBLEM[audience];
  return (
    <section id="problema" className="py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-[var(--muted)]">
            El problema
          </p>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-[var(--foreground)] sm:text-4xl">
            {title}
          </h2>
          <p className="mt-4 text-lg text-[var(--muted)]">{intro}</p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2">
          {pains.map((pain) => {
            const Icon = pain.icon;
            return (
              <div
                key={pain.title}
                className="rounded-2xl border border-[var(--separator)] bg-[var(--surface)] p-7"
              >
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-xl"
                  style={{ background: `color-mix(in oklch, ${PAIN_TINT} 15%, transparent)` }}
                >
                  <Icon className="h-6 w-6" style={{ color: PAIN_TINT }} />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-[var(--foreground)]">
                  {pain.title}
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[var(--muted)]">{pain.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
